/**
 * Pre-computed flat search index for hierarchical tree data.
 *
 * Builds a flat array of indexed nodes once when tree data loads (~5ms for 5000+ nodes).
 * On search, scans the flat array (pre-lowercased labels, no allocations) and returns
 * a Set<string> of visible node IDs. The original tree is never cloned — visibility
 * is controlled by Set membership in the render pass.
 */

interface SearchableNode {
  id: string;
  label: string;
  children?: SearchableNode[];
}

export interface IndexedNode {
  id: string;
  lowerLabel: string;
  /**
   * Lowercased words in the label, split on non-word characters.
   * Pre-computed once at index time so search can do cheap
   * `startsWith` checks for word-boundary matching.
   */
  words: string[];
  ancestorIds: string[];
  descendantIds: string[];
}

// Hardcoded splitter — splits on any run of non-word chars (whitespace,
// punctuation, parens). Safe from ReDoS: no quantifier alternation.
const WORD_SPLIT_RE = /[^a-z0-9_]+/;

export interface TreeSearchIndex {
  nodes: IndexedNode[];
}

/**
 * Build a flat search index from a tree. Runs once when data loads.
 * Pre-computes lowercase labels, ancestor paths, and descendant sets.
 */
export function buildTreeSearchIndex<T extends SearchableNode>(
  tree: T[]
): TreeSearchIndex {
  const nodes: IndexedNode[] = [];

  const collectDescendantIds = (children: SearchableNode[]): string[] => {
    const ids: string[] = [];
    for (const child of children) {
      ids.push(child.id);
      if (child.children && child.children.length > 0) {
        ids.push(...collectDescendantIds(child.children));
      }
    }
    return ids;
  };

  const indexNode = (node: SearchableNode, ancestors: string[]): void => {
    const children = node.children || [];
    const descendantIds = collectDescendantIds(children);
    const lowerLabel = node.label.toLowerCase();
    const words = lowerLabel.split(WORD_SPLIT_RE).filter(Boolean);

    nodes.push({
      id: node.id,
      lowerLabel,
      words,
      ancestorIds: [...ancestors],
      descendantIds,
    });

    for (const child of children) {
      indexNode(child, [...ancestors, node.id]);
    }
  };

  for (const root of tree) {
    indexNode(root, []);
  }

  return { nodes };
}

/**
 * Search the pre-computed index and return visible node IDs.
 * Returns null if the search term is empty (meaning "show all").
 * Returns a Set<string> of node IDs that should be visible.
 *
 * Matching rule: word-boundary prefix. The term must appear at the
 * start of some word in the label. Typing "ink" will match "ink" or
 * "india ink" but not "pink" / "clink" / "blinking" — raw substring
 * matching surfaces too much noise on a 5000-node vocabulary.
 */
export function searchTreeIndex(
  index: TreeSearchIndex,
  searchTerm: string
): Set<string> | null {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return null;

  const visible = new Set<string>();

  for (const node of index.nodes) {
    // Word-boundary prefix match: at least one of the label's pre-split
    // words must start with the term. No dynamic RegExp — avoids ReDoS
    // and is also faster than `\b` regex per node on large vocabularies.
    let matched = false;
    for (const word of node.words) {
      if (word.startsWith(term)) {
        matched = true;
        break;
      }
    }
    if (matched) {
      // Add the matching node itself
      visible.add(node.id);

      // Add all ancestors (so the path to this node is visible)
      for (const ancestorId of node.ancestorIds) {
        visible.add(ancestorId);
      }

      // Add all descendants (show children of matching parent)
      for (const descendantId of node.descendantIds) {
        visible.add(descendantId);
      }
    }
  }

  return visible;
}
